import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterMatchMode } from "primereact/api";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { PlotMouseEvent } from "plotly.js";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks";
import {
    selectFilteredDefects,
    selectPscRiskFilterBy,
    selectRegimeMappings,
    selectRegimeMappingsError,
    selectRegimeMappingsLoaded,
    selectRegimeMappingsLoading,
    selectSelectedVessel
} from "../../store/selectors/defectSelectors";
import {
    selectSelectedTechnicalManagers,
    selectSelectedVesselAgeBands,
    selectSelectedVessels,
    selectSelectedVesselTypes
} from "../../store/selectors/filtersSelectors";
import { fetchRegimeMappings } from "../../store/thunks/defectThunks";
import { resetTableFilters } from "../../store/thunks/filtersThunks";
import { setActiveTab, setPscRiskFilterBy } from "../../store/slices/defectSlice";
import { DefectTabs } from "../../types/DefectTabs";
import { DefectInsightFilterBy } from "../../types/DefectInsightsData";
import LOCAL_TABLE_CONFIG_KEY from "../../types/TableLocalConfigKeys";
import { defaultDefectsFiltersConfig } from "../DefectsTable/Filters/FilterTemplates";
import { DefectTableFields } from "../DefectsTable/DefectTableFields";
import {
    buildPscRiskIndicators,
    MAX_PSC_RISKS,
    PscRiskGroupBy
} from "../../utils/PscRiskGraphHelper";
import { sendGAEvent } from "../../AnalyticsService";
import {
    DEFECTS_PSC_RISK,
    DEFECTS_PSC_RISK_VIEW_TECHNICAL_MANAGERS,
    DEFECTS_PSC_RISK_VIEW_VESSELS
} from "../../constants/analytics";
import { PscRiskGraph } from "./PscRiskGraph";

export const PscRiskTab = () => {
    const { t } = useTranslation("locale");
    const dispatch = useAppDispatch();

    const defects = useAppSelector(selectFilteredDefects);
    const mappings = useAppSelector(selectRegimeMappings);
    const loading = useAppSelector(selectRegimeMappingsLoading);
    const loaded = useAppSelector(selectRegimeMappingsLoaded);
    const error = useAppSelector(selectRegimeMappingsError);
    const filterBy = useAppSelector(selectPscRiskFilterBy);
    const selectedVessel = useAppSelector(selectSelectedVessel);
    const selectedVessels = useAppSelector(selectSelectedVessels);
    const selectedVesselAgeBands = useAppSelector(selectSelectedVesselAgeBands);
    const selectedVesselTypes = useAppSelector(selectSelectedVesselTypes);
    const selectedTechnicalManagers = useAppSelector(
        selectSelectedTechnicalManagers
    );

    // Gated on `loaded` rather than on the mappings themselves: an empty
    // result is a valid answer, and keying off it would fetch forever.
    useEffect(() => {
        if (!loaded && !loading) {
            dispatch(fetchRegimeMappings());
        }
    }, [loaded, loading, dispatch]);

    /**
     * The bars only split once the view has been narrowed, matching the
     * Insights graphs - unfiltered, the legend would list the whole fleet.
     */
    const groupBy: PscRiskGroupBy | undefined = useMemo(() => {
        const filtered =
            !!selectedVessel ||
            selectedVessels.length > 0 ||
            selectedVesselAgeBands.length > 0 ||
            selectedVesselTypes.length > 0 ||
            selectedTechnicalManagers.length > 0;

        if (!filtered) {
            return undefined;
        }

        return filterBy === DefectInsightFilterBy.TECHNICAL_MANAGERS
            ? "technicalManager"
            : "vesselName";
    }, [
        filterBy,
        selectedVessel,
        selectedVessels,
        selectedVesselAgeBands,
        selectedVesselTypes,
        selectedTechnicalManagers
    ]);

    const risks = useMemo(
        () =>
            buildPscRiskIndicators(
                defects,
                mappings,
                MAX_PSC_RISKS,
                groupBy,
                t("defectsGraph.pscRiskUnknownGroup")
            ),
        [defects, mappings, groupBy, t]
    );

    const options = useMemo(
        () =>
            Object.values(DefectInsightFilterBy).map((option) => ({
                label: t(`defectInsights.${option}`),
                value: option
            })),
        [t]
    );

    const onChangeFilterBy = useCallback(
        (event: DropdownChangeEvent) => {
            dispatch(setPscRiskFilterBy(event.value));
            sendGAEvent(
                DEFECTS_PSC_RISK,
                event.value === DefectInsightFilterBy.VESSELS
                    ? DEFECTS_PSC_RISK_VIEW_VESSELS
                    : DEFECTS_PSC_RISK_VIEW_TECHNICAL_MANAGERS
            );
        },
        [dispatch]
    );

    const onBarClick = useCallback(
        (event: Readonly<PlotMouseEvent>) => {
            if (!event.points.length) {
                return;
            }

            const { data, pointIndex } = event.points[0];
            // The table filters on Idwal codes, not the PSC code.
            const idwalCodes = data.customdata?.[pointIndex];

            if (!Array.isArray(idwalCodes) || !idwalCodes.length) {
                return;
            }

            const tableFilters: Record<string, unknown> = {
                ...defaultDefectsFiltersConfig,
                [DefectTableFields.STATUS]: {
                    value: null,
                    matchMode: FilterMatchMode.IN
                },
                [DefectTableFields.TYPE_CODE]: {
                    value: idwalCodes,
                    matchMode: FilterMatchMode.IN
                }
            };

            // A stacked segment belongs to one vessel / technical manager,
            // named by trace.name, so the click through carries that too.
            // Both match exactly: nameFilterField is "{name} {imo}" matched by
            // substring, so "Aurora" would drag in "Northern Aurora" as well.
            if (groupBy && data.name) {
                tableFilters[
                    groupBy === "technicalManager"
                        ? DefectTableFields.TECHNICAL_MANAGER
                        : DefectTableFields.VESSEL_NAME
                ] = {
                    value: [data.name],
                    matchMode: FilterMatchMode.IN
                };
            }

            dispatch(
                resetTableFilters(LOCAL_TABLE_CONFIG_KEY.DEFECTS, tableFilters)
            );

            dispatch(setActiveTab(DefectTabs.defects));
        },
        [dispatch, groupBy]
    );

    return (
        <div>
            <div className="flex flex-row justify-content-end mb-4">
                <div className="flex flex-row align-items-center">
                    <p>{t("defectInsights.show")}</p>
                    <Dropdown
                        value={filterBy}
                        onChange={onChangeFilterBy}
                        options={options}
                        className="md:w-15rem ml-3"
                        data-cy="psc-risk-graph-dropdown"
                    />
                </div>
            </div>
            <div className="flex align-items-center justify-content-between">
                <h2>{t("defectsGraph.pscRiskIndicators")}</h2>
                <div className="flex align-items-center">
                    <span>{t("defectsGraph.pscRiskSource")}</span>
                    {/* psc-risk-tooltip escapes the global nowrap rule - see App.css */}
                    <Tooltip
                        target=".psc-risk-info"
                        className="psc-risk-tooltip"
                        position="left"
                    />
                    <i
                        className="pi pi-info-circle psc-risk-info ml-2"
                        data-pr-tooltip={t("defectsGraph.pscRiskInfo")}
                    />
                </div>
            </div>
            <PscRiskGraph
                risks={risks}
                loading={loading}
                error={error}
                onClick={onBarClick}
                legendText={t(`defectInsights.${filterBy}`)}
            />
        </div>
    );
};
